import React, { createContext, useContext, useCallback, useRef, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { createLogger } from '../utils/logger';
import { hybridCache } from '../utils/hybridCache';
import { smartCacheManager } from '../utils/smartCacheInvalidation';
import { backgroundSyncManager } from '../utils/backgroundSync';
import { offlineFirstCache } from '../utils/offlineFirstCache';

const logger = createLogger({ module: 'DataCacheContext' });

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  userId: string | null;
}

interface CacheMap {
  [key: string]: CacheEntry<unknown>;
}

interface CacheResult<T> {
  data: T | null;
  status: 'valid' | 'expired' | 'user_changed' | 'not_found' | 'stale_offline' | 'stale_fallback' | 'fresh';
  age?: number;
  maxAge?: number;
  source?: 'l1_cache' | 'l2_cache' | 'legacy_cache' | 'miss' | 'network' | 'offline_cache' | 'fallback_cache';
}

interface CacheOptions {
  ttl?: number;
  dependencies?: string[];
  version?: string;
  useHybridCache?: boolean; // Flag to enable hybrid cache for specific operations
  syncToServer?: boolean;
  table?: string;
  operation?: string;
}

interface DataCacheContextType {
  getData: <T>(key: string, maxAge?: number) => CacheResult<T>;
  setData: <T>(key: string, data: T, options?: CacheOptions) => Promise<void>;
  invalidateCache: (key?: string) => Promise<void>;
  setCacheData: <T>(key: string, data: T) => void;
  // New hybrid cache methods
  getDataAsync: <T>(key: string, options?: CacheOptions) => Promise<CacheResult<T>>;
  getStaleData: <T>(key: string) => Promise<T | null>;
  preloadData: <T>(key: string, fetchFn: () => Promise<T>, options?: CacheOptions) => Promise<T>;
  getCacheStats: () => Promise<any>;
  healthCheck: () => Promise<any>;
  // New sync methods
  getSyncStatus: () => any;
  forceSyncNow: () => Promise<void>;
  // New offline-first methods
  getWithOfflineSupport: <T>(
    key: string,
    fetchFn?: () => Promise<T>,
    options?: {
      allowStale?: boolean;
      maxStaleAge?: number;
      backgroundRefresh?: boolean;
    }
  ) => Promise<{
    data: T | null;
    status: string;
    source: string;
    isStale?: boolean;
  }>;
  setWithOfflineSupport: <T>(
    key: string,
    data: T,
    options?: {
      syncToServer?: boolean;
      table?: string;
      operation?: 'create' | 'update' | 'delete';
      priority?: number;
      optimisticUpdate?: boolean;
    }
  ) => Promise<void>;
  syncOfflineOperations: () => Promise<{
    synced: number;
    failed: number;
    conflicts: number;
  }>;
  getOfflineOperationsCount: () => number;
  clearOfflineOperations: () => void;
  // Request deduplication methods
  getInFlightRequest: <T>(key: string) => Promise<T> | null;
  setInFlightRequest: <T>(key: string, promise: Promise<T>) => void;
  removeInFlightRequest: (key: string) => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

export const DataCacheProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const legacyCacheRef = useRef<CacheMap>({});
  const inFlightRequestsRef = useRef<Map<string, Promise<any>>>(new Map());
  const { user } = useAuth();
  const [isHybridCacheAvailable, setIsHybridCacheAvailable] = useState(false);

  // Check hybrid cache availability on mount
  useEffect(() => {
    const checkHybridCache = async () => {
      try {
        const health = await hybridCache.healthCheck();
        setIsHybridCacheAvailable(health.overall);
        logger.debug('Hybrid cache availability checked', { available: health.overall });
      } catch (error) {
        logger.warn('Failed to check hybrid cache availability', { error });
        setIsHybridCacheAvailable(false);
      }
    };

    checkHybridCache();
  }, []);

  // Register cache dependencies on mount
  useEffect(() => {
    if (!user) return;

    // Register common cache dependencies
    smartCacheManager.registerDependency({
      key: 'user-unified-content',
      dependsOn: ['notes', 'career-cards', 'reviews'],
      refreshFn: async () => {
        // This will be called by the refresh functions in Home.tsx
        return null;
      }
    });

    smartCacheManager.registerDependency({
      key: 'timeline-data',
      dependsOn: ['notes', 'career-cards', 'reviews', 'files'],
      refreshFn: async () => {
        // Timeline data refresh function
        return null;
      }
    });

    smartCacheManager.registerDependency({
      key: 'user-settings',
      dependsOn: ['user-profile'],
      refreshFn: async () => {
        // Settings refresh function
        return null;
      }
    });

    // Preload dependencies for better performance
    smartCacheManager.preloadDependencies(user.id).catch(error => {
      logger.warn('Failed to preload cache dependencies:', error);
    });

    logger.debug('Cache dependencies registered for user', { 
      userId: user.id.substring(0, 8) 
    });
  }, [user]);

  // Legacy getData method for backward compatibility
  const getData = useCallback(<T,>(key: string, maxAge: number = 5 * 60 * 1000): CacheResult<T> => {
    logger.debug('Getting data from legacy cache:', { key, maxAge });
    const entry = legacyCacheRef.current[key] as CacheEntry<T> | undefined;
    
    if (!entry) {
      logger.debug('No legacy cache entry found:', { key });
      return { data: null, status: 'not_found', source: 'legacy_cache' };
    }

    // Invalidate if user changed
    if (entry.userId !== user?.id) {
      logger.debug('Legacy cache invalidated due to user change:', { key, cacheUserId: entry.userId, currentUserId: user?.id });
      delete legacyCacheRef.current[key];
      return { data: null, status: 'user_changed', source: 'legacy_cache' };
    }

    // Check if data is stale
    const age = Date.now() - entry.timestamp;
    if (age > maxAge) {
      logger.debug('Legacy cache entry expired:', { key, age, maxAge });
      delete legacyCacheRef.current[key];
      return { data: null, status: 'expired', age, maxAge, source: 'legacy_cache' };
    }

    logger.debug('Returning legacy cached data:', { 
      key, 
      dataType: typeof entry.data,
      isArray: Array.isArray(entry.data),
      length: Array.isArray(entry.data) ? entry.data.length : null
    });
    return { 
      data: entry.data, 
      status: 'valid',
      age,
      maxAge,
      source: 'legacy_cache'
    };
  }, [user?.id]);

  // New async getData method using hybrid cache
  const getDataAsync = useCallback(async <T,>(key: string, options: CacheOptions = {}): Promise<CacheResult<T>> => {
    if (!isHybridCacheAvailable || !user?.id || !options.useHybridCache) {
      // Fall back to legacy cache
      return getData<T>(key, options.ttl || 5 * 60 * 1000);
    }

    try {
      const result = await hybridCache.get<T>(key, user.id);
      
      logger.debug('Hybrid cache result:', { 
        key, 
        status: result.status,
        source: result.source,
        age: result.age 
      });
      
      return {
        data: result.data,
        status: result.status,
        age: result.age,
        source: result.source
      };
    } catch (error) {
      logger.error('Hybrid cache get failed, falling back to legacy:', { key, error });
      return getData<T>(key, options.ttl || 5 * 60 * 1000);
    }
  }, [isHybridCacheAvailable, user?.id, getData]);

  // Enhanced setData method with hybrid cache support
  const setData = useCallback(async <T,>(
    key: string, 
    data: T, 
    options: CacheOptions = {}
  ): Promise<void> => {
    const userId = user?.id || 'anonymous';
    
    try {
      if (options.useHybridCache && isHybridCacheAvailable) {
        await hybridCache.set(key, data, userId, options);
      } else {
        // Fallback to legacy cache
        setCacheData(key, data);
      }
      
      // Queue for background sync if sync options provided
      if (options.syncToServer && options.table && options.operation) {
        backgroundSyncManager.queueOperation({
          type: options.operation,
          table: options.table,
          data,
          userId
        });
      }
      
      logger.debug('Data cached successfully', { 
        key, 
        useHybridCache: options.useHybridCache && isHybridCacheAvailable,
        syncQueued: !!(options.syncToServer && options.table && options.operation)
      });
    } catch (error) {
      logger.error('Failed to cache data', { key, error });
      throw error;
    }
  }, [user?.id, isHybridCacheAvailable]);

  // Enhanced invalidateCache method
  const invalidateCache = useCallback(async (key?: string): Promise<void> => {
    const userId = user?.id || 'anonymous';
    
    try {
      if (key) {
        // Use smart cache manager for dependency-aware invalidation
        await smartCacheManager.invalidateWithDependencies(key, userId);
        
        if (isHybridCacheAvailable) {
          await hybridCache.invalidate(key, userId);
        } else {
          // Fallback to legacy cache
          if (legacyCacheRef.current[key] && legacyCacheRef.current[key].userId === userId) {
            delete legacyCacheRef.current[key];
          }
        }
      } else {
        // Clear all user cache
        if (isHybridCacheAvailable) {
          await hybridCache.clear(userId);
        } else {
          // Clear legacy cache for user
          Object.keys(legacyCacheRef.current).forEach(cacheKey => {
            if (legacyCacheRef.current[cacheKey].userId === userId) {
              delete legacyCacheRef.current[cacheKey];
            }
          });
        }
      }
      
      logger.debug('Cache invalidated', { key: key || 'all', userId });
    } catch (error) {
      logger.error('Failed to invalidate cache', { key, error });
    }
  }, [user?.id, isHybridCacheAvailable]);

  // Legacy setCacheData for backward compatibility
  const setCacheData = useCallback(<T,>(key: string, data: T) => {
    logger.debug('Setting legacy cache data (sync):', { key });
    legacyCacheRef.current[key] = {
      data,
      timestamp: Date.now(),
      userId: user?.id || null
    };
  }, [user?.id]);

  // New method to get stale data for offline scenarios
  const getStaleData = useCallback(async <T,>(key: string): Promise<T | null> => {
    if (!isHybridCacheAvailable || !user?.id) {
      // Fall back to legacy cache
      const entry = legacyCacheRef.current[key] as CacheEntry<T> | undefined;
      return entry?.data || null;
    }

    try {
      return await hybridCache.getStaleData<T>(key, user.id);
    } catch (error) {
      logger.error('Failed to get stale data from hybrid cache:', { key, error });
      // Fall back to legacy cache
      const entry = legacyCacheRef.current[key] as CacheEntry<T> | undefined;
      return entry?.data || null;
    }
  }, [isHybridCacheAvailable, user?.id]);

  // New method to preload data into cache
  const preloadData = useCallback(async <T,>(
    key: string, 
    fetchFn: () => Promise<T>, 
    options: CacheOptions = {}
  ): Promise<T> => {
    if (!isHybridCacheAvailable || !user?.id || !options.useHybridCache) {
      // Execute fetch function and store in legacy cache
      const data = await fetchFn();
      await setData(key, data, options);
      return data;
    }

    try {
      return await hybridCache.preload(key, user.id, fetchFn, {
        ttl: options.ttl,
        dependencies: options.dependencies,
        version: options.version
      });
    } catch (error) {
      logger.error('Failed to preload data in hybrid cache:', { key, error });
      // Fall back to legacy approach
      const data = await fetchFn();
      await setData(key, data, options);
      return data;
    }
  }, [isHybridCacheAvailable, user?.id, setData]);

  // New method to get cache statistics
  const getCacheStats = useCallback(async () => {
    const stats: any = {
      legacyCache: {
        totalKeys: Object.keys(legacyCacheRef.current).length,
        userKeys: Object.keys(legacyCacheRef.current).filter(key => 
          legacyCacheRef.current[key].userId === user?.id
        ).length
      },
      hybridCache: null,
      isHybridCacheAvailable
    };

    if (isHybridCacheAvailable && user?.id) {
      try {
        stats.hybridCache = await hybridCache.getCacheStats(user.id);
      } catch (error) {
        logger.error('Failed to get hybrid cache stats:', error);
        stats.hybridCache = { error: error instanceof Error ? error.message : String(error) };
      }
    }

    return stats;
  }, [isHybridCacheAvailable, user?.id]);

  // New method for health check
  const healthCheck = useCallback(async () => {
    const health: any = {
      legacyCache: {
        available: true,
        totalKeys: Object.keys(legacyCacheRef.current).length
      },
      hybridCache: null,
      overall: true
    };

    if (isHybridCacheAvailable) {
      try {
        health.hybridCache = await hybridCache.healthCheck();
        health.overall = health.hybridCache.overall;
      } catch (error) {
        logger.error('Hybrid cache health check failed:', error);
        health.hybridCache = { 
          available: false, 
          error: error instanceof Error ? error.message : String(error) 
        };
        health.overall = false;
      }
    }

    return health;
  }, [isHybridCacheAvailable]);

  // Add sync status monitoring
  const getSyncStatus = useCallback(() => {
    return backgroundSyncManager.getSyncStatus();
  }, []);

  // Add force sync capability
  const forceSyncNow = useCallback(async () => {
    await backgroundSyncManager.forceSyncNow();
  }, []);

  // Add offline-first methods
  const getWithOfflineSupport = useCallback(async <T,>(
    key: string,
    fetchFn?: () => Promise<T>,
    options: {
      allowStale?: boolean;
      maxStaleAge?: number;
      backgroundRefresh?: boolean;
    } = {}
  ) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      return await offlineFirstCache.getWithOfflineSupport(
        key,
        user.id,
        fetchFn,
        options
      );
    } catch (error) {
      console.warn('Offline-first cache get failed, falling back to legacy cache:', error);
      const legacyResult = getData<T>(key);
      return {
        data: legacyResult.data,
        status: legacyResult.status,
        source: 'legacy_fallback',
        isStale: legacyResult.status !== 'valid'
      };
    }
  }, [user?.id, getData]);

  const setWithOfflineSupport = useCallback(async <T,>(
    key: string,
    data: T,
    options: {
      syncToServer?: boolean;
      table?: string;
      operation?: 'create' | 'update' | 'delete';
      priority?: number;
      optimisticUpdate?: boolean;
    } = {}
  ) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    try {
      await offlineFirstCache.setWithOfflineSupport(
        key,
        data,
        user.id,
        options
      );
    } catch (error) {
      console.warn('Offline-first cache set failed, falling back to legacy cache:', error);
      await setData(key, data);
    }
  }, [user?.id, setData]);

  const syncOfflineOperations = useCallback(async () => {
    try {
      return await offlineFirstCache.syncOfflineOperations();
    } catch (error) {
      console.error('Failed to sync offline operations:', error);
      return { synced: 0, failed: 0, conflicts: 0 };
    }
  }, []);

  const getOfflineOperationsCount = useCallback(() => {
    try {
      return offlineFirstCache.getOfflineOperationsCount();
    } catch (error) {
      console.warn('Failed to get offline operations count:', error);
      return 0;
    }
  }, []);

  const clearOfflineOperations = useCallback(() => {
    try {
      offlineFirstCache.clearOfflineOperations();
    } catch (error) {
      console.warn('Failed to clear offline operations:', error);
    }
  }, []);

  // Request deduplication methods
  const getInFlightRequest = useCallback(<T,>(key: string): Promise<T> | null => {
    return inFlightRequestsRef.current.get(key) || null;
  }, []);

  const setInFlightRequest = useCallback(<T,>(key: string, promise: Promise<T>): void => {
    logger.debug('Setting in-flight request', { key });
    inFlightRequestsRef.current.set(key, promise);
  }, []);

  const removeInFlightRequest = useCallback((key: string): void => {
    logger.debug('Removing in-flight request', { key });
    inFlightRequestsRef.current.delete(key);
  }, []);

  const contextValue: DataCacheContextType = {
    getData,
    setData,
    invalidateCache,
    setCacheData,
    getDataAsync,
    getStaleData,
    preloadData,
    getCacheStats,
    healthCheck,
    getSyncStatus,
    forceSyncNow,
    // New offline-first methods
    getWithOfflineSupport,
    setWithOfflineSupport,
    syncOfflineOperations,
    getOfflineOperationsCount,
    clearOfflineOperations,
    // Request deduplication
    getInFlightRequest,
    setInFlightRequest,
    removeInFlightRequest
  };

  return (
    <DataCacheContext.Provider value={contextValue}>
      {children}
    </DataCacheContext.Provider>
  );
};

export const useDataCache = () => {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}; 